import type {
  NamedRef,
  OperationalUserDetails,
  OperationalUserStatus,
} from "../domain";

const JEDDAH: NamedRef = {
  id: "branch-jeddah",
  nameEn: "Jeddah Branch",
  nameAr: "فرع جدة",
};
const RIYADH: NamedRef = {
  id: "branch-riyadh",
  nameEn: "Riyadh Branch",
  nameAr: "فرع الرياض",
};
const MAKKAH: NamedRef = {
  id: "branch-makkah",
  nameEn: "Makkah Branch",
  nameAr: "فرع مكة المكرمة",
};
const MADINAH: NamedRef = {
  id: "branch-madinah",
  nameEn: "Madinah Branch",
  nameAr: "فرع المدينة المنورة",
};
const DAMMAM: NamedRef = {
  id: "branch-dammam",
  nameEn: "Dammam Branch",
  nameAr: "فرع الدمام",
};
const TAIF: NamedRef = {
  id: "branch-taif",
  nameEn: "Taif Branch",
  nameAr: "فرع الطائف",
};
const ABHA: NamedRef = {
  id: "branch-abha",
  nameEn: "Abha Branch",
  nameAr: "فرع أبها",
};

const MAIN_DEPT: NamedRef = {
  id: "department-main",
  nameEn: "Head Office Department",
  nameAr: "قسم المكتب الرئيسي",
};
const OPERATIONS_DEPT: NamedRef = {
  id: "department-operations",
  nameEn: "Operations Department",
  nameAr: "قسم العمليات",
};

function user(
  seq: number,
  nameEn: string,
  nameAr: string,
  username: string,
  branch: NamedRef,
  department: NamedRef,
  status: OperationalUserStatus,
  permissions: readonly string[],
): OperationalUserDetails {
  const suffix = String(seq).padStart(3, "0");
  return {
    id: `operational-user-${suffix}`,
    nameEn,
    nameAr,
    username,
    email: `${username}@nasam.sa`,
    mobileNumber1: seq % 4 === 0 ? null : `+96658345${String(1100 + seq)}`,
    branch,
    department,
    status,
    permissions,
  };
}

const GENERAL_MANAGER_PERMISSIONS: readonly string[] = [
  "core.organization_management.organization_management_list.view",
  "core.organization_management.organization_management_list.create",
  "core.organization_management.organization_management_list.update",
  "core.organization_management.organization_management_list.delete",
];
const SUPERVISOR_PERMISSIONS: readonly string[] = [
  "core.organization_management.organization_management_list.view",
];
const ANALYST_PERMISSIONS: readonly string[] = [
  "core.organization_management.organization_management_list.view",
];

/** 14 operational users (two pages at the default 10): a mix of active,
 *  inactive and pending-activation across the three sample roles. */
export const operationalUserFixtures: readonly OperationalUserDetails[] = [
  user(
    1,
    "Mohammed bin Abdullah Al-Harthy",
    "محمد بن عبدالله الحارثي",
    "m.alharthy",
    JEDDAH,
    MAIN_DEPT,
    "ACTIVE",
    GENERAL_MANAGER_PERMISSIONS,
  ),
  user(
    2,
    "Khalid Faisal Al-Ghamdi",
    "خالد فيصل الغامدي",
    "k.alghamdi",
    RIYADH,
    OPERATIONS_DEPT,
    "INACTIVE",
    SUPERVISOR_PERMISSIONS,
  ),
  user(
    3,
    "Sarah Abdulrahman Al-Mutairi",
    "سارة عبدالرحمن المطيري",
    "s.almutairi",
    MAKKAH,
    OPERATIONS_DEPT,
    "PENDING_ACTIVATION",
    ANALYST_PERMISSIONS,
  ),
  user(
    4,
    "Abdulaziz Mohammed Al-Qahtani",
    "عبدالعزيز محمد القحطاني",
    "a.alqahtani",
    MADINAH,
    MAIN_DEPT,
    "INACTIVE",
    GENERAL_MANAGER_PERMISSIONS,
  ),
  user(
    5,
    "Mona Abdullah",
    "منى عبدالله",
    "m.abdullah",
    TAIF,
    OPERATIONS_DEPT,
    "ACTIVE",
    GENERAL_MANAGER_PERMISSIONS,
  ),
  user(
    6,
    "Fahad Hussein",
    "فهد حسين",
    "f.hussein",
    ABHA,
    MAIN_DEPT,
    "INACTIVE",
    SUPERVISOR_PERMISSIONS,
  ),
  user(
    7,
    "Jameel Ahmed",
    "جميل أحمد",
    "j.ahmed",
    JEDDAH,
    OPERATIONS_DEPT,
    "INACTIVE",
    GENERAL_MANAGER_PERMISSIONS,
  ),
  user(
    8,
    "Noura Saeed Al-Dosari",
    "نورة سعيد الدوسري",
    "n.aldosari",
    RIYADH,
    MAIN_DEPT,
    "ACTIVE",
    ANALYST_PERMISSIONS,
  ),
  user(
    9,
    "Turki Nasser Al-Otaibi",
    "تركي ناصر العتيبي",
    "t.alotaibi",
    DAMMAM,
    OPERATIONS_DEPT,
    "ACTIVE",
    SUPERVISOR_PERMISSIONS,
  ),
  user(
    10,
    "Lama Khalid Al-Shehri",
    "لمى خالد الشهري",
    "l.alshehri",
    MAKKAH,
    MAIN_DEPT,
    "PENDING_ACTIVATION",
    ANALYST_PERMISSIONS,
  ),
  user(
    11,
    "Bandar Saad Al-Qarni",
    "بندر سعد القرني",
    "b.alqarni",
    JEDDAH,
    OPERATIONS_DEPT,
    "ACTIVE",
    SUPERVISOR_PERMISSIONS,
  ),
  user(
    12,
    "Reem Fahad Al-Zahrani",
    "ريم فهد الزهراني",
    "r.alzahrani",
    RIYADH,
    MAIN_DEPT,
    "ACTIVE",
    GENERAL_MANAGER_PERMISSIONS,
  ),
  user(
    13,
    "Yousef Omar Al-Harbi",
    "يوسف عمر الحربي",
    "y.alharbi",
    TAIF,
    OPERATIONS_DEPT,
    "INACTIVE",
    ANALYST_PERMISSIONS,
  ),
  user(
    14,
    "Hind Majed Al-Amri",
    "هند ماجد العمري",
    "h.alamri",
    ABHA,
    MAIN_DEPT,
    "ACTIVE",
    SUPERVISOR_PERMISSIONS,
  ),
];

export interface ArchivedOperationalUserFixture extends OperationalUserDetails {
  readonly archivedAt: string;
}

export const archivedOperationalUserFixtures: readonly ArchivedOperationalUserFixture[] =
  [
    {
      ...user(
        101,
        "Salem Youssef Al-Malki",
        "سالم يوسف المالكي",
        "s.almalki",
        JEDDAH,
        OPERATIONS_DEPT,
        "INACTIVE",
        ANALYST_PERMISSIONS,
      ),
      archivedAt: "2026-06-12T09:00:00.000Z",
    },
    {
      ...user(
        102,
        "Amal Nasser Al-Subaie",
        "أمل ناصر السبيعي",
        "a.alsubaie",
        RIYADH,
        MAIN_DEPT,
        "INACTIVE",
        SUPERVISOR_PERMISSIONS,
      ),
      archivedAt: "2026-05-20T09:00:00.000Z",
    },
  ];
